// externals
import { createSelector } from 'reselect';

// libraries
import type { StateTree } from '@makemydeal/dr-dash-types';
import { isSalesCheckInName, PENDING_DEALER_REVIEW_PAYMENT_STATUS } from '@makemydeal/dr-shared-types';

// selectors
import { getCalculationDetailsState } from './calculationDetailsSelectors';
import { getSocketEventNotification, getPendingReview, getPendingReviewResolution } from './offerInfoSelectors';
import * as dealHistorySelectors from './dealHistorySelectors';
import * as vehicleSelectors from './vehicleSelectors';

export const getPaymentCalculationStatus = (state: StateTree): string | undefined =>
    getCalculationDetailsState(state).payment?.calculationState?.paymentStatus;

export const hasPendingDealerReviewStatus = (state: StateTree): boolean =>
    getPaymentCalculationStatus(state) === PENDING_DEALER_REVIEW_PAYMENT_STATUS;

// Not `getSocketEventAppName`: that returns a display name, falling back to "Showroom" for Sales Check-In,
// so it never matches an application name.
export const getSocketVersionApplicationName = (state: StateTree): string | undefined =>
    getSocketEventNotification(state)?.dealStructure?.versionCreatedBy?.applicationName;

export const getSocketVersionPaymentStatus = (state: StateTree): string | undefined =>
    getSocketEventNotification(state)?.dealStructure?.financialDetails?.calculationDetails?.payment?.calculationState
        ?.paymentStatus;

export const isSocketVersionPendingReview = (state: StateTree): boolean =>
    getSocketVersionPaymentStatus(state) === PENDING_DEALER_REVIEW_PAYMENT_STATUS;

export const isSocketVersionPaymentSettled = (state: StateTree): boolean => {
    const paymentStatus = getSocketVersionPaymentStatus(state);

    return paymentStatus !== undefined && paymentStatus !== PENDING_DEALER_REVIEW_PAYMENT_STATUS;
};

export const isPendingDealerReview = (state: StateTree): boolean => hasPendingDealerReviewStatus(state);

export const isResolvingPendingReview = (state: StateTree): boolean => getPendingReview(state)?.isResolving === true;

export const shouldSuppressSystemInitiatedPricing = (state: StateTree): boolean =>
    getPendingReviewResolution(state) === undefined && (isPendingDealerReview(state) || isResolvingPendingReview(state));

// Deliberately not gated on the loaded deal being pending: that would be false in the primary scenario —
// a dealer on their own non-pending version when SCI publishes. It decides only whether to go and look.
export const isPendingDealerReviewFromSocket = (state: StateTree): boolean =>
    isSalesCheckInName(getSocketVersionApplicationName(state));

/** Mirrors the change-vehicle activity's `VehicleInfo`; dash-store must not depend on an activity. */
export type PendingReviewVehicleInfo = {
    year: number;
    make: string;
    model: string;
    trim: string;
    stockNumber?: string;
    condition?: string;
};

const toVehicleInfo = (vehicle: any): PendingReviewVehicleInfo | undefined =>
    vehicle
        ? {
              year: vehicle.year || 0,
              make: vehicle.make || '',
              model: vehicle.model || '',
              trim: vehicle.trim || '',
              stockNumber: vehicle.stockNumber,
              condition: vehicle.condition
          }
        : undefined;

// Not `offers[1]`: consecutive Sales Check-In revisions stack, so the second history record is frequently
// another SCI version rather than anything the dealer worked on. Undefined until MV has saved once, which
// a caller must not read as "unchanged".
export const getPreChangeVehicleInfo = createSelector(
    // Wrapped, not passed by reference: createSelector would bind at module load, defeating any spy.
    [(state: StateTree) => dealHistorySelectors.getLatestDealerOffer(state)],
    (latestMvOffer): PendingReviewVehicleInfo | undefined =>
        latestMvOffer === undefined ? undefined : toVehicleInfo(latestMvOffer.vehicle)
);

export const getPostChangeVehicleInfo = createSelector([(state: StateTree) => vehicleSelectors.getVehicle(state)], (vehicle) =>
    toVehicleInfo(vehicle)
);
